import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { letterRegApi } from '../lib/api';
import { Button } from './ui/button';
import { formatDate } from '../lib/utils';
import { FileCheck2 } from 'lucide-react';

/**
 * Sits beside Pending Documents and tells HR who has completed probation and
 * still has no confirmation letter. It only notifies — the letter is never
 * created automatically.
 *
 * The alert has a one-week life: the backend only returns people whose
 * confirmation date falls inside the last CONFIRMATION_WINDOW days, so a row
 * ages out by itself on day 8. It also excludes anyone who already has a
 * confirmation letter, so generating one drops the row on the spot.
 *
 * The backend returns 403 to anyone without letters.generate, so the panel
 * stays invisible for people who could not act on it anyway.
 */
const CONFIRMATION_WINDOW = 7;

export default function ConfirmationDuePanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    letterRegApi.confirmationDue(CONFIRMATION_WINDOW)
      .then((r) => { if (live) setData(r.data || null); })
      .catch(() => {});          // 403 = not a letter-issuing user; stay silent
    return () => { live = false; };
  }, []);

  if (!data || !data.count) return null;

  return (
    <div data-testid="confirmation-due-panel"
         className="glass-card card-lift reveal w-full lg:w-[400px] flex-shrink-0 p-4 !border-[#FDE68A]">
      <div className="flex items-center gap-2.5">
        <span className="icon-tile tile-warn w-8 h-8"><FileCheck2 className="w-4 h-4" strokeWidth={2} /></span>
        <div className="font-display text-sm font-bold text-[#11182B]">Confirmation Due</div>
        <span data-testid="confirmation-due-count" className="pill pill-warn">
          {data.count}
        </span>
      </div>
      <p className="text-[11px] text-[#5A6377] mt-1">
        Completed {data.months} months in the last {data.window_days} days · no letter generated yet
      </p>

      <div className="mt-3 max-h-[260px] overflow-auto space-y-2">
        {data.employees.map((e) => (
          <div key={e.id}
               data-testid={`confirmation-due-${e.id}`}
               className="rounded-2xl border border-white/80 bg-white/70 backdrop-blur-sm p-2.5 flex items-start justify-between gap-2 transition-all duration-200 hover:bg-white hover:shadow-soft hover:-translate-y-0.5">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-[#11182B] truncate">
                {e.name} {e.card_no && <span className="text-[#98A1B2] font-normal">· {e.card_no}</span>}
              </div>
              <div className="text-[11px] text-[#5A6377] truncate">
                {[e.designation, e.department, e.unit].filter(Boolean).join(' · ')}
              </div>
              <div className="text-[11px] text-[#5A6377] mt-0.5">
                Joined <b>{formatDate(e.doj)}</b> · Confirmed{' '}
                <b className="text-[#1D4ED8]">{formatDate(e.confirmation_date)}</b>
              </div>
            </div>
            <Link to={`/employees/${e.id}?tab=letters&letter=confirmation`}>
              <Button size="sm" className="whitespace-nowrap h-7 px-3 text-[11px]">Generate</Button>
            </Link>
          </div>
        ))}
      </div>

      {data.older_count > 0 && (
        <p className="text-[11px] text-[#98A1B2] mt-2">
          {data.older_count} longer-serving employee{data.older_count === 1 ? '' : 's'} also have no
          {' '}confirmation letter (completed over {data.window_days} days ago).
        </p>
      )}
    </div>
  );
}
